import { getSiloV2MarketEntry, siloV2LenderKey } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import {
  fetchSiloMarketsLite,
  SILO_API_SUPPORTED_CHAIN_IDS,
  type SiloApiEntry,
} from '../../../lending/public-data/silo-v2/fetchPublic'
import type {
  OraclePriceEntry,
  ParseContext,
  TokenListMap,
  USDPriceMap,
} from '../types'

/**
 * Narrowed Silo v2 oracle-price prefetch via `api-v3.silo.finance`.
 *
 * The `silos.market{1,2}.solvencyOracle.quote` field already exposes the
 * same "1 baseToken in quoteToken units" value the on-chain `ISiloOracle.quote`
 * call produces, so when the indexer has data for a chain we can skip the
 * Silo v2 multicall entirely and emit the price entries from the GraphQL
 * response instead.
 *
 * This mirrors the Morpho GraphQL prefetch in `morpho.ts`: the prefetch
 * runs in parallel with the on-chain multicall, and the caller decides —
 * after both are settled — which source to feed into the parse pipeline.
 *
 * Returns `null` when the indexer has no data for the chain (caller must
 * fall back to on-chain) and throws on transport / GraphQL errors so the
 * caller can log and fall back.
 */
export async function fetchSiloV2GraphQLMarkets(
  chainId: string,
): Promise<SiloApiEntry[] | null> {
  if (!SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)) return null
  try {
    const items = await fetchSiloMarketsLite(chainId, { protocolVersion: 'v2' })
    return items
  } catch (e) {
    console.warn(
      `[prices] Silo v2 GraphQL fetch failed on chain ${chainId}:`,
      (e as any)?.message ?? e,
    )
    return null
  }
}

/**
 * Morpho-style one-shot price fetcher: returns parsed `OraclePriceEntry[]`
 * (or `null` to signal "fall back to on-chain") in a single call.
 *
 * Self-contained — does NOT depend on prices populated by other lender
 * parsers. Empty silos that need a quote-token USD lookup are resolved
 * against the union of:
 *   1. USD prices self-derived from `supplyUsd / supply` across every
 *      non-empty silo half in the same GraphQL response (covers USDC,
 *      USDT, WETH, etc. since they show up as widely deposited assets).
 *   2. The caller-provided `basePrices` map (from `fetchOraclePrices`'s
 *      `basePrices` option).
 *
 * Mirrors `fetchMorphoGraphQLPrices` so the caller can treat both
 * protocols uniformly: parallel fetch, push entries directly, fall back
 * to the on-chain multicall when this returns `null`.
 */
export async function fetchSiloV2GraphQLPrices(
  chainId: string,
  basePrices: USDPriceMap = {},
  tokenList: TokenListMap = {},
): Promise<OraclePriceEntry[] | null> {
  const items = await fetchSiloV2GraphQLMarkets(chainId)
  if (items == null) return null

  const usdPrices = buildSelfDerivedUsdMap(chainId, items, basePrices, tokenList)
  const context: ParseContext = { chainId, usdPrices, tokenList }
  const entries = parseSiloV2GraphQLResults(items, context)
  if (entries.length === 0) {
    // `items.length === 0` is the indexer simply having nothing for this chain
    // — a routine state, not a parse failure. Only warn when markets came back
    // and NONE of them yielded a price, which does mean the shaping broke.
    if (items.length > 0)
      console.warn(
        `[silo-v2-gql] chain ${chainId}: ${items.length} markets fetched but 0 price entries produced`,
      )
    return null
  }
  return entries
}

/**
 * Build a USD-per-token map from `supplyUsd / supply` (or
 * `borrowedUsd / borrowed`) across every silo half in the response, then
 * overlay `basePrices` so caller-provided overrides win for tokens that
 * happen to appear in both.
 *
 * Keyed by both the asset's `assetGroup` (when known) and its lowercased
 * address, matching `lookupUSD`'s probe order.
 */
function buildSelfDerivedUsdMap(
  chainId: string,
  items: SiloApiEntry[],
  basePrices: USDPriceMap,
  tokenList: TokenListMap,
): USDPriceMap {
  const map: USDPriceMap = {}

  const set = (tokenLc: string, priceUSD: number) => {
    if (!Number.isFinite(priceUSD) || priceUSD === 0) return
    const groupKey =
      tokenList[tokenLc]?.assetGroup ?? `${chainId}-${tokenLc}`
    if (map[groupKey] == null) map[groupKey] = priceUSD
    if (map[tokenLc] == null) map[tokenLc] = priceUSD
  }

  for (const item of items) {
    for (const market of [item.market1, item.market2]) {
      if (!market) continue
      const tokenLc = (
        market.inputTokenId ??
        market.inputToken?.id ??
        ''
      ).toLowerCase()
      if (!tokenLc) continue

      const supply = safeNumber(market.supply)
      const supplyUsd =
        market.supplyUsd != null ? Number(market.supplyUsd) : 0
      if (supply > 0 && supplyUsd > 0) {
        set(tokenLc, supplyUsd / supply)
        continue
      }

      const borrowed = safeNumber(market.borrowed)
      const borrowedUsd =
        market.borrowedUsd != null ? Number(market.borrowedUsd) : 0
      if (borrowed > 0 && borrowedUsd > 0) {
        set(tokenLc, borrowedUsd / borrowed)
      }
    }
  }

  // Caller-provided basePrices win for any tokens they cover.
  for (const [k, v] of Object.entries(basePrices)) {
    if (Number.isFinite(v) && v > 0) map[k] = v
  }

  return map
}

function safeNumber(v: string | number | null | undefined): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function lookupUSD(context: ParseContext, asset: string): number {
  const lc = asset.toLowerCase()
  const groupKey =
    context.tokenList?.[lc]?.assetGroup ?? `${context.chainId}-${lc}`
  return context.usdPrices[groupKey] ?? context.usdPrices[lc] ?? 0
}

/**
 * Convert a GraphQL-fetched silos list into oracle price entries.
 *
 * For each silo half:
 *  - If the solvencyOracle is the zero address → emit a `staticBase` entry
 *    priced via the partner token's USD (mirrors the on-chain parser).
 *  - Otherwise, `priceUSD = Number(quote) * quoteTokenUSD`. The raw price
 *    surfaced to consumers is `Number(quote)` so downstream math matches
 *    the on-chain output.
 *
 * Entries whose quote token has no resolved USD are skipped — the parse
 * order in `fetchOraclePrices.ts` must run this AFTER the base-USD tier
 * (Aave / Chainlink / Compound) so the cross-asset prices are available.
 */
export function parseSiloV2GraphQLResults(
  items: SiloApiEntry[],
  context: ParseContext,
): OraclePriceEntry[] {
  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
  const out: OraclePriceEntry[] = []
  const seen = new Set<string>()

  for (const item of items) {
    let lenderKey: string
    try {
      lenderKey = siloV2LenderKey(item.configAddress)
    } catch {
      continue
    }

    // Look up the registry entry so oracle prices can be emitted against
    // the same silo addresses the on-chain path uses. Without it the
    // downstream price consumers (which look up by silo address) would
    // miss every market.
    const registryEntry = getSiloV2MarketEntry(context.chainId, lenderKey)
    if (!registryEntry) continue

    // Align GraphQL market1/market2 to registry silo0/silo1 by index.
    const [apiSide0, apiSide1] =
      item.market1.index === 0
        ? [item.market1, item.market2]
        : [item.market2, item.market1]

    const sideTuples = [
      { api: apiSide0, self: registryEntry.silo0, other: registryEntry.silo1 },
      { api: apiSide1, self: registryEntry.silo1, other: registryEntry.silo0 },
    ]

    for (const { api: side, self, other } of sideTuples) {
      const tokenLc = (
        side.inputTokenId ??
        side.inputToken?.id ??
        self.token
      ).toLowerCase()
      if (!tokenLc) continue

      const partnerTokenLc = other.token.toLowerCase()

      const dedupKey = `${lenderKey}:${tokenLc}`
      if (seen.has(dedupKey)) continue

      // Silo marks zero-address oracle halves as constant 1.0 — fall back
      // to the partner token's USD (or our own, if known from elsewhere).
      const oracleAddr = (
        side.solvencyOracleAddress ??
        side.solvencyOracle?.id ??
        self.solvencyOracle ??
        ZERO_ADDRESS
      ).toLowerCase()
      const isStatic = !oracleAddr || oracleAddr === ZERO_ADDRESS

      const siloAddrLc = self.silo.toLowerCase()

      try {
        let priceUSD = 0
        let rawPrice = 1

        // Primary resolution: the indexer's `supply` / `supplyUsd` pair
        // already gives us a direct USD-per-token ratio without needing
        // any cross-asset price lookup. Fall back to the quote-based path
        // only when the silo is empty on this side (no supply = no usd).
        const supplyNum = safeNumber(side.supply)
        const supplyUsdNum =
          side.supplyUsd != null ? Number(side.supplyUsd) : 0
        const borrowedNum = safeNumber(side.borrowed)
        const borrowedUsdNum =
          side.borrowedUsd != null ? Number(side.borrowedUsd) : 0

        if (supplyNum > 0 && supplyUsdNum > 0) {
          priceUSD = supplyUsdNum / supplyNum
          rawPrice = isStatic ? 1 : priceUSD
        } else if (borrowedNum > 0 && borrowedUsdNum > 0) {
          priceUSD = borrowedUsdNum / borrowedNum
          rawPrice = isStatic ? 1 : priceUSD
        } else if (isStatic) {
          // Empty static side — fall back to the partner token's USD
          // (static = price is 1.0 in the partner unit).
          const ownUSD = lookupUSD(context, tokenLc)
          const partnerUSD = partnerTokenLc
            ? lookupUSD(context, partnerTokenLc)
            : 0
          priceUSD = ownUSD || partnerUSD
          if (!priceUSD) continue
          rawPrice = 1
        } else {
          // Non-static, empty silo — derive USD via the oracle quote and
          // the quote token's known USD (populated by the base-price tier
          // earlier in the parse pipeline).
          const quoteStr = side.solvencyOracle?.quote
          if (!quoteStr) continue
          const quoteNum = Number(quoteStr)
          if (!Number.isFinite(quoteNum) || quoteNum === 0) continue

          const quoteTokenLc = (
            side.solvencyOracle?.quoteTokenId ?? ''
          ).toLowerCase()
          if (!quoteTokenLc) continue

          const qtUSD = lookupUSD(context, quoteTokenLc)
          if (!qtUSD) continue

          rawPrice = quoteNum
          priceUSD = quoteNum * qtUSD
        }

        if (!Number.isFinite(priceUSD) || priceUSD === 0) continue

        out.push({
          asset: tokenLc,
          price: rawPrice,
          priceUSD,
          marketUid: createMarketUid(context.chainId, lenderKey, siloAddrLc),
          targetLender: lenderKey,
          ...(isStatic ? { staticBase: true, baseAsset: tokenLc } : {}),
        })
        seen.add(dedupKey)
      } catch {
        // skip bad entries
      }
    }
  }

  return out
}
